import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { JSONContent } from "@tiptap/core";
import type { FileUpload } from "./FileUpload.ts";

export enum RFQReminderType {
    ONCE = "ONCE",
    RECURRING = "RECURRING",
}

export enum RFQReminderStatus {
    DRAFT = "DRAFT",
    SCHEDULED = "SCHEDULED",
    SENT = "SENT",
}

export type RFQReminderAttachment = {
    id: number;
    createdAt: string;

    // Relationships
    fileId: number;
    file: FileUpload;
    reminderId: number;
    reminder: RFQReminder;
};

export interface RFQReminder {
    id: number;
    name: string;
    content: string;
    contentJson: JSONContent;
    status: RFQReminderStatus;
    type: RFQReminderType;
    scheduledAt?: string;
    scheduledInterval?: string;
    startAt?: string;
    endAt?: string;
    remainingActions?: number;
    lastSentAt?: string;
    scheduleId: string;
    createdAt: string;
    archivedAt?: string;
    rfqId: number;
    attachments: RFQReminderAttachment[];
}

export type RFQReminderInput = {
    name?: string;
    content: string;
    contentJson?: JSONContent;
    type: RFQReminderType;
    scheduledAt?: string;
    scheduledInterval?: string;
    startAt?: string;
    endAt?: string;
    remainingActions?: number | null;
    senderId?: number | null;
    purchaseOrderId?: number | null;
};

export default {
    getRFQReminder: (rfqPublicId: string, reminderId: number): Promise<RFQReminder> => {
        /**
         * This method will fetch reminders for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders/${reminderId}`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to get RFQ reminder");
            }
            return res.json();
        });
    },

    createRFQReminder: (rfqPublicId: string, data: RFQReminderInput) => {
        /**
         * This method will create a reminder for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to create RFQ reminder");
            }
            return res.json();
        });
    },

    updateRFQReminder: (rfqPublicId: string, id: number, data: Partial<RFQReminderInput>) => {
        /**
         * This method will update a reminder for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders/${id}`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ reminder");
            }
            return res.json();
        });
    },

    deleteRFQReminder: (rfqPublicId: string, id: number) => {
        /**
         * This method will delete a reminder for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders/${id}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to delete RFQ reminder");
            }
            return res.json();
        });
    },

    addFilesToReminder: (rfqPublicId: string, id: number, fileIds: number[]) => {
        /**
         * This method will update a reminder for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders/${id}/attachments`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ fileIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to add files to RFQ reminder");
            }
            return res.json();
        });
    },

    removeFileFromReminder: (rfqPublicId: string, id: number, attachmentId: number) => {
        /**
         * This method will update a reminder for an RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/reminders/${id}/attachments/${attachmentId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to remove file from RFQ reminder");
            }
            return res.json();
        });
    },
};
